import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronRight, ArrowLeft, Check, Sparkles, Lightbulb, AlertCircle, RefreshCw, Info, AlertTriangle, Lock } from 'lucide-react';
import { api } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import { showToast } from '../../../utils/toastManager';
import '../../../styles/resume-editor.css';
import '../../../styles/ai-reviewer-suggestions.css';

interface AICVTask {
  uid: string;
  user_uid: string;
  cv_uid: string;
  entity_type: 'summary' | 'work_experience_block' | 'work_experience_achievement' | 'volunteer_experience_block' | 'volunteer_experience_achievement' | 'skills_list' | 'skills_groups' | 'projects_block' | 'projects_description' | 'projects_achievement';
  entity_uid: string;
  improvement_type: 'current' | 'new' | 'delete';
  ai_task_uid: string;
  is_implemented: boolean;
  old_value: { value: any[] } | null;
  new_value: { value: any[] } | null;
  comment: { value: any[] } | null;
  company_name?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface WorkExperience {
  uid: string;
  employer_name: string;
  job_title: string;
  start_date: string | null;
  end_date: string | null;
  is_current: boolean;
  location?: string;
  description?: string;
  achievements?: string[];
  skills?: string[];
}

interface Project {
  uid: string;
  title: string;
  headline: string;
  link?: string;
  linkAlias?: string;
  description?: string;
  releaseDate?: string | null;
  yearOnly?: boolean;
  hide?: boolean;
  achievements?: string[];
}

interface AIReviewerSuggestionsProps {
  cvUid: string;
  workExperiences?: WorkExperience[];
  volunteerExperiences?: WorkExperience[];
  projects?: Project[];
  onSectionClick?: (section: string) => void;
  onTextClick?: (fieldName: string) => void;
  onImplement?: (task: AICVTask, mode?: 'append' | 'replace') => void | Promise<void>;
}

const PROJECTS_AI_ENTITY_TYPES: string[] = ['projects_block', 'projects_description', 'projects_achievement'];

export const AIReviewerSuggestions: React.FC<AIReviewerSuggestionsProps> = ({ cvUid, workExperiences = [], volunteerExperiences = [], projects = [], onSectionClick, onTextClick, onImplement }) => {
  const [tasks, setTasks] = useState<AICVTask[]>([]);
  const visibleTasks = tasks.filter((t) => !PROJECTS_AI_ENTITY_TYPES.includes(t.entity_type));
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<AICVTask | null>(null);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [implementing, setImplementing] = useState<boolean>(false);
  const [showImplementMenu, setShowImplementMenu] = useState<boolean>(false);
  const [runningFullReview, setRunningFullReview] = useState<boolean>(false);

  const reloadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get<AICVTask[]>(API_ENDPOINTS.aiCv.getTasks(cvUid));
      setTasks(data || []);
    } catch (err: any) {
      console.error('Failed to fetch AI CV tasks:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load AI suggestions');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    await reloadTasks();
  };

  const runFullReview = async () => {
    if (!cvUid || runningFullReview) return;

    const payloads: Array<Record<string, unknown>> = [];
    payloads.push({ cv_uid: cvUid, entity_type: 'summary', improvement_type: 'new' });
    payloads.push({ cv_uid: cvUid, entity_type: 'skills_list', improvement_type: 'current' });
    workExperiences.forEach(we => {
      if (!we.uid) return;
      payloads.push({
        cv_uid: cvUid,
        entity_type: 'work_experience_block',
        entity_uid: we.uid,
        improvement_type: 'current',
      });
    });
    volunteerExperiences.forEach(ve => {
      if (!ve.uid) return;
      payloads.push({
        cv_uid: cvUid,
        entity_type: 'volunteer_experience_block',
        entity_uid: ve.uid,
        improvement_type: 'current',
      });
    });

    setRunningFullReview(true);
    try {
      const results = await Promise.allSettled(
        payloads.map(p => api.post(API_ENDPOINTS.aiCv.createTask, p)),
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;

      if (succeeded > 0) {
        showToast(
          failed === 0
            ? `AI Review queued for ${succeeded} section${succeeded === 1 ? '' : 's'}.`
            : `AI Review queued for ${succeeded} section${succeeded === 1 ? '' : 's'} (${failed} failed — check your plan limits).`,
          failed === 0 ? 'success' : 'info',
        );
      } else {
        const first = results.find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
        const detail = (first?.reason as any)?.data?.detail || (first?.reason as any)?.message;
        showToast(detail || 'Could not start AI Review. Please try again.', 'error');
      }

      await reloadTasks();
    } finally {
      setRunningFullReview(false);
    }
  };

  useEffect(() => {
    reloadTasks();
  }, [cvUid]);

  useEffect(() => {
    (window as any).__refreshAITasks = () => reloadTasks();
    return () => {
      delete (window as any).__refreshAITasks;
    };
  }, [cvUid]);

  const tasksRef = useRef(tasks);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  const hasPendingTasks = useMemo(
    () => tasks.some(t => t.status === 'created' || t.status === 'launched'),
    [tasks],
  );

  useEffect(() => {
    if (!cvUid || !hasPendingTasks) return;

    const MAX_ATTEMPTS = 60;
    let attempts = 0;

    const interval = window.setInterval(async () => {
      attempts += 1;
      if (attempts > MAX_ATTEMPTS) {
        window.clearInterval(interval);
        return;
      }
      try {
        const data = await api.get<AICVTask[]>(API_ENDPOINTS.aiCv.getTasks(cvUid));
        if (!Array.isArray(data)) return;
        setTasks(data);
        const stillPending = data.some(
          t => t.status === 'created' || t.status === 'launched',
        );
        if (!stillPending) window.clearInterval(interval);
      } catch {
      }
    }, 4000);

    return () => window.clearInterval(interval);
  }, [cvUid, hasPendingTasks]);


  const getEntityTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      summary: 'Professional Summary',
      work_experience_block: 'Work Experience',
      work_experience_achievement: 'Work Achievement',
      volunteer_experience_block: 'Volunteer Experience',
      volunteer_experience_achievement: 'Volunteer Achievement',
      skills_list: 'Skills List',
      skills_groups: 'Skills Categories',
      projects_block: 'Projects',
      projects_description: 'Project Description',
      projects_achievement: 'Project Achievement',
    };
    return labels[type] || type;
  };

  const mapEntityTypeToSection = (entityType: string): string => {
    const mapping: Record<string, string> = {
      summary: 'summary',
      work_experience_block: 'experience',
      work_experience_achievement: 'experience',
      volunteer_experience_block: 'volunteer',
      volunteer_experience_achievement: 'volunteer',
      skills_list: 'skills',
      skills_groups: 'skills',
      projects_block: 'projects',
      projects_description: 'projects',
      projects_achievement: 'projects',
    };
    return mapping[entityType] || 'summary';
  };

  const mapEntityTypeToFieldName = (entityType: string, entityUid?: string): string => {
    if (entityType === 'summary') {
      return 'summary';
    }
    if ((entityType === 'work_experience_block' || entityType === 'work_experience_achievement') && entityUid) {
      return `experience.${entityUid}`;
    }
    if ((entityType === 'volunteer_experience_block' || entityType === 'volunteer_experience_achievement') && entityUid) {
      return `volunteer.${entityUid}`;
    }
    if ((entityType === 'projects_block' || entityType === 'projects_description' || entityType === 'projects_achievement') && entityUid) {
      return `projects.${entityUid}`;
    }
    if (entityType === 'skills_list' || entityType === 'skills_groups') {
      return 'skills';
    }
    return entityType;
  };

  const getImprovementTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      current: 'Improve',
      new: 'New',
      delete: 'Remove',
    };
    return labels[type] || type;
  };

  const getStatusTooltip = (status: string): string => {
    const tooltips: Record<string, string> = {
      created: 'Task is created and waiting to be processed',
      in_progress: 'Task is currently being processed',
      completed: 'Task has been completed successfully',
      failed: 'Task processing failed',
    };
    return tooltips[status] || 'Unknown status';
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      created: '#f59e0b',
      in_progress: '#f59e0b',
      completed: '#10b981',
      failed: '#ef4444',
    };
    return colors[status] || '#6b7280';
  };

  const extractTextFromValue = (value: { value: any[] } | null | undefined | string): string => {
    if (!value) {
      return '';
    }
    
    if (typeof value === 'string') {
      return value;
    }
    
    if (typeof value === 'object' && 'value' in value) {
      const valueArray = value.value;
      
      if (Array.isArray(valueArray)) {
        return valueArray
          .map((item: any) => {
            if (typeof item === 'string') {
              return item;
            }
            if (typeof item === 'object' && item !== null && 'value' in item) {
              return item.value || '';
            }
            return String(item || '');
          })
          .filter((text: string) => text.trim() !== '')
          .join('\n');
      }
      
      return String(valueArray || '');
    }
    
    return String(value || '');
  };

  const extractComments = (comment: { value: any[] } | null | undefined): Array<{ type: string; comment: string }> => {
    if (!comment || typeof comment !== 'object' || !('value' in comment)) {
      return [];
    }
    
    const valueArray = comment.value;
    if (!Array.isArray(valueArray)) {
      return [];
    }
    
    return valueArray
      .filter((item: any) => item && typeof item === 'object' && 'comment' in item)
      .map((item: any) => ({
        type: item.type || 'info',
        comment: item.comment || ''
      }))
      .filter((item: { type: string; comment: string }) => item.comment.trim() !== '');
  };

  const renderValueAsBullets = (value: { value: any[] } | null | undefined): React.ReactNode => {
    if (!value || typeof value !== 'object' || !('value' in value)) {
      return null;
    }

    const valueArray = value.value;
    if (!Array.isArray(valueArray)) {
      return null;
    }

    return (
      <div className="ai-reviewer-suggestions__new-value-list">
        {valueArray.map((item: any, index: number) => {
          if (typeof item === 'object' && item !== null && 'value' in item) {
            const itemValue = item.value || '';
            return (
              <div key={index} className="ai-reviewer-suggestions__new-value-bullet">
                <span className="ai-reviewer-suggestions__bullet-point">•</span>
                <span className="ai-reviewer-suggestions__bullet-text">{itemValue}</span>
              </div>
            );
          }
          return (
            <div key={index} className="ai-reviewer-suggestions__new-value-plain">
              {String(item || '')}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSkillsListValue = (value: { value: any[] } | null | undefined): React.ReactNode => {
    if (!value || typeof value !== 'object' || !('value' in value)) {
      return null;
    }

    const valueArray = value.value;
    if (!Array.isArray(valueArray)) {
      return null;
    }

    return (
      <div className="ai-reviewer-suggestions__new-value-list">
        {valueArray.map((item: any, index: number) => {
          if (typeof item === 'object' && item !== null && 'value' in item) {
            const itemValue = String(item.value ?? '');
            const category = item.skill_category != null && String(item.skill_category).trim() !== ''
              ? String(item.skill_category).trim()
              : null;
            return (
              <div key={index} className="ai-reviewer-suggestions__new-value-bullet">
                <span className="ai-reviewer-suggestions__bullet-point">•</span>
                <span className="ai-reviewer-suggestions__bullet-text">
                  {category != null ? (
                    <>
                      <span className="ai-reviewer-suggestions__skill-category">{category}</span>
                      <span className="ai-reviewer-suggestions__skill-category-sep">: </span>
                      <span className="ai-reviewer-suggestions__skill-value">{itemValue}</span>
                    </>
                  ) : (
                    itemValue
                  )}
                </span>
              </div>
            );
          }
          return (
            <div key={index} className="ai-reviewer-suggestions__new-value-bullet">
              <span className="ai-reviewer-suggestions__bullet-point">•</span>
              <span className="ai-reviewer-suggestions__bullet-text">{String(item ?? '')}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderNewValue = (newValue: { value: any[] } | null | undefined, entityType: string): React.ReactNode => {
    if (!newValue) {
      return null;
    }

    if (entityType === 'skills_list' && typeof newValue === 'object' && 'value' in newValue) {
      return renderSkillsListValue(newValue);
    }

    if (
      (entityType === 'summary' || entityType === 'work_experience_block' || entityType === 'volunteer_experience_block' || entityType === 'skills_groups') &&
      typeof newValue === 'object' &&
      'value' in newValue
    ) {
      return renderValueAsBullets(newValue);
    }

    return <span>{extractTextFromValue(newValue)}</span>;
  };

  const fetchTaskDetails = async (taskUid: string) => {
    try {
      setLoadingDetails(true);
      setError(null);
      const taskDetails = await api.get<AICVTask>(API_ENDPOINTS.aiCv.getTaskDetails(taskUid));
      setSelectedTask(taskDetails);
    } catch (err: any) {
      console.error('Failed to fetch AI CV task details:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load task details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const markTaskAsCompleted = async (taskUid: string) => {
    try {
      await api.put(API_ENDPOINTS.aiCv.markCompleted(taskUid));
      await fetchTaskDetails(taskUid);
      await fetchTasks();
    } catch (err: any) {
      console.error('Failed to mark task as completed:', err);
      throw err;
    }
  };

  const getEntityDisplayName = (task: AICVTask): React.ReactNode => {
    const baseLabel = getEntityTypeLabel(task.entity_type);
    
    if (task.entity_type === 'work_experience_achievement' || task.entity_type === 'work_experience_block') {
      const workExp = workExperiences.find(exp => exp.uid === task.entity_uid);
      const companyName = workExp?.employer_name || task.company_name;
      if (companyName) {
        return (
          <>
            {baseLabel} <span className="ai-reviewer-suggestions__company-name">at {companyName}</span>
          </>
        );
      }
    }
    
    if (task.entity_type === 'volunteer_experience_achievement' || task.entity_type === 'volunteer_experience_block') {
      const volunteerExp = volunteerExperiences.find(exp => exp.uid === task.entity_uid);
      const companyName = volunteerExp?.employer_name || task.company_name;
      if (companyName) {
        return (
          <>
            {baseLabel} <span className="ai-reviewer-suggestions__company-name">at {companyName}</span>
          </>
        );
      }
    }
    
    if (task.entity_type === 'projects_achievement' || task.entity_type === 'projects_description') {
      const project = projects.find(p => p.uid === task.entity_uid);
      const projectName = project?.title || task.company_name || 'Unknown Project';
      return (
        <>
          {baseLabel}: <span className="ai-reviewer-suggestions__company-name">{projectName}</span>
        </>
      );
    }
    
    if (task.entity_type === 'projects_block') {
      const project = projects.find(p => p.uid === task.entity_uid);
      const projectName = project?.title || task.company_name || 'Unknown Project';
      return (
        <>
          Project Achievements: <span className="ai-reviewer-suggestions__company-name">{projectName}</span>
        </>
      );
    }
    return baseLabel;
  };

  if (loading) {
    return (
      <div className="ai-reviewer-suggestions">
        <div className="ai-reviewer-suggestions__loading">Loading suggestions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-reviewer-suggestions">
        <div className="ai-reviewer-suggestions__error">
          <div className="ai-reviewer-suggestions__error-icon">
            <AlertCircle size={48} />
          </div>
          <h3 className="ai-reviewer-suggestions__error-title">Unable to Load Suggestions</h3>
          <p className="ai-reviewer-suggestions__error-message">{error}</p>
          <button
            className="ai-reviewer-suggestions__retry-btn"
            onClick={fetchTasks}
          >
            <RefreshCw size={16} />
            <span>Try Again</span>
          </button>
        </div>
      </div>
    );
  }

  if (selectedTask) {
    const isWorkAchievement = selectedTask.entity_type === 'work_experience_achievement';
    const isVolunteerAchievement = selectedTask.entity_type === 'volunteer_experience_achievement';

    const canImplementSingleAchievement = (
      task: AICVTask,
      experiences: WorkExperience[] | undefined,
    ): { canImplement: boolean; tooltip?: string } => {
      const isWork = task.entity_type === 'work_experience_achievement';
      const isVolunteer = task.entity_type === 'volunteer_experience_achievement';
      if (!isWork && !isVolunteer) {
        return { canImplement: true };
      }

      const exp = experiences?.find((e) => e.uid === task.entity_uid);
      if (!exp || !Array.isArray(exp.achievements) || exp.achievements.length === 0) {
        return {
          canImplement: false,
          tooltip: 'Could not find this achievement in the current list – unable to replace it.',
        };
      }

      const oldTextFull = extractTextFromValue(task.old_value as any);
      const oldFirst = oldTextFull
        .split('\n')
        .map((t) => t.trim())
        .find((t) => t.length > 0);

      if (!oldFirst) {
        return {
          canImplement: false,
          tooltip: 'Could not find this achievement in the current list – unable to replace it.',
        };
      }

      const exists = exp.achievements.some((a) => (a || '').trim() === oldFirst);

      if (!exists) {
        return {
          canImplement: false,
          tooltip: 'Could not find this achievement in the current list – unable to replace it.',
        };
      }

      return { canImplement: true };
    };

    const workAchievementState = canImplementSingleAchievement(selectedTask, workExperiences);
    const volunteerAchievementState = canImplementSingleAchievement(selectedTask, volunteerExperiences);

    return (
      <div className="ai-reviewer-suggestions">
        <div className="ai-reviewer-suggestions__header">
          <h3 
            className="ai-reviewer-suggestions__title ai-reviewer-suggestions__title--clickable"
            onClick={() => {
              const isExperienceDetail =
                (selectedTask.entity_type === 'work_experience_block' || selectedTask.entity_type === 'work_experience_achievement' ||
                 selectedTask.entity_type === 'volunteer_experience_block' || selectedTask.entity_type === 'volunteer_experience_achievement') &&
                !selectedTask.is_implemented;
              if (isExperienceDetail) {
                const fieldName = mapEntityTypeToFieldName(selectedTask.entity_type, selectedTask.entity_uid);
                onTextClick?.(fieldName);
              } else {
                const section = mapEntityTypeToSection(selectedTask.entity_type);
                onSectionClick?.(section);
              }
            }}
          >
            {getEntityTypeLabel(selectedTask.entity_type)}
          </h3>
          <span className="ai-reviewer-suggestions__type-badge">
            {getImprovementTypeLabel(selectedTask.improvement_type)}
          </span>
        </div>

        <div className="ai-reviewer-suggestions__detail">
          {loadingDetails ? (
            <div className="ai-reviewer-suggestions__loading">Loading details...</div>
          ) : (
            <div className="ai-reviewer-suggestions__detail-section">
              <div className={`ai-reviewer-suggestions__detail-text-wrapper ${selectedTask.is_implemented ? 'ai-reviewer-suggestions__detail-text-wrapper--implemented' : ''}`}>
                {selectedTask.old_value && (
                  <>
                    {selectedTask.entity_type === 'skills_list' ? (
                      <div
                        className={`ai-reviewer-suggestions__detail-text ai-reviewer-suggestions__detail-text--old ai-reviewer-suggestions__detail-text--clickable ai-reviewer-suggestions__detail-text--old-block`}
                        onClick={() => {
                          if (!selectedTask.is_implemented) {
                            const fieldName = mapEntityTypeToFieldName(selectedTask.entity_type, selectedTask.entity_uid);
                            onTextClick?.(fieldName);
                          }
                        }}
                      >
                        {renderSkillsListValue(selectedTask.old_value)}
                      </div>
                    ) : (selectedTask.entity_type === 'work_experience_block' || selectedTask.entity_type === 'volunteer_experience_block' || selectedTask.entity_type === 'skills_groups') ? (
                      <div
                        className={`ai-reviewer-suggestions__detail-text ai-reviewer-suggestions__detail-text--old ai-reviewer-suggestions__detail-text--clickable ai-reviewer-suggestions__detail-text--old-block`}
                        onClick={() => {
                          if (!selectedTask.is_implemented) {
                            const fieldName = mapEntityTypeToFieldName(selectedTask.entity_type, selectedTask.entity_uid);
                            onTextClick?.(fieldName);
                          }
                        }}
                      >
                        {renderValueAsBullets(selectedTask.old_value)}
                      </div>
                    ) : (
                      <span 
                        className={`ai-reviewer-suggestions__detail-text ai-reviewer-suggestions__detail-text--old ${!selectedTask.is_implemented ? 'ai-reviewer-suggestions__detail-text--clickable' : ''}`}
                        onClick={() => {
                          if (!selectedTask.is_implemented) {
                            const fieldName = mapEntityTypeToFieldName(selectedTask.entity_type, selectedTask.entity_uid);
                            onTextClick?.(fieldName);
                          }
                        }}
                      >
                        {extractTextFromValue(selectedTask.old_value)}
                      </span>
                    )}
                    {' '}
                  </>
                )}
                {selectedTask.new_value && (
                  <div 
                    className={`ai-reviewer-suggestions__detail-text ai-reviewer-suggestions__detail-text--new ${!selectedTask.is_implemented ? 'ai-reviewer-suggestions__detail-text--clickable' : ''}`}
                    onClick={() => {
                      if (!selectedTask.is_implemented) {
                        const fieldName = mapEntityTypeToFieldName(selectedTask.entity_type, selectedTask.entity_uid);
                        onTextClick?.(fieldName);
                      }
                    }}
                  >
                    {renderNewValue(selectedTask.new_value, selectedTask.entity_type)}
                  </div>
                )}
                {selectedTask.comment && (
                  <div className="ai-reviewer-suggestions__detail-comments">
                    {extractComments(selectedTask.comment).map((commentItem, index) => (
                      <div 
                        key={index}
                        className={`ai-reviewer-suggestions__comment-tile ai-reviewer-suggestions__comment-tile--${commentItem.type}`}
                      >
                        <div className="ai-reviewer-suggestions__comment-tile-type">
                          {commentItem.type === 'info' && <Info size={18} />}
                          {commentItem.type === 'attention' && <AlertTriangle size={18} />}
                          {commentItem.type === 'important' && <AlertCircle size={18} />}
                        </div>
                        <div className="ai-reviewer-suggestions__comment-tile-text">
                          {commentItem.comment}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="ai-reviewer-suggestions__actions">
          <button
            className="ai-reviewer-suggestions__back-btn"
            onClick={() => {
              setSelectedTask(null);
              setShowImplementMenu(false);
            }}
          >
            <ArrowLeft size={16} />
            <span>Back</span>
          </button>
          {selectedTask && (selectedTask.entity_type === 'work_experience_block' || selectedTask.entity_type === 'volunteer_experience_block' || selectedTask.entity_type === 'skills_groups') && !selectedTask.is_implemented ? (
            <div
              className="ai-reviewer-suggestions__implement-wrapper"
              onMouseEnter={() => setShowImplementMenu(true)}
              onMouseLeave={() => setShowImplementMenu(false)}
            >
              <button
                className="ai-reviewer-suggestions__implement-btn"
                disabled={implementing}
              >
                <Sparkles size={16} />
                <span>{implementing ? 'Applying...' : 'Implement'}</span>
              </button>
              {showImplementMenu && (
                <div className="ai-reviewer-suggestions__implement-menu">
                  <button
                    className="ai-reviewer-suggestions__implement-menu-item"
                    disabled={implementing}
                    onClick={async () => {
                      if (!onImplement || !selectedTask) return;
                      setImplementing(true);
                      try {
                        await onImplement(selectedTask, 'replace');
                        await markTaskAsCompleted(selectedTask.uid);
                        setTasks(prev =>
                          prev.map(task =>
                            task.uid === selectedTask.uid
                              ? { ...task, is_implemented: true, status: 'completed' }
                              : task
                          )
                        );
                        setSelectedTask(null);
                      } catch (error) {
                        console.error('Failed to replace with AI suggestion:', error);
                      } finally {
                        setImplementing(false);
                        setShowImplementMenu(false);
                      }
                    }}
                  >
                    {selectedTask.entity_type === 'skills_groups' ? 'Replace list' : 'Replace list'}
                  </button>
                  <button
                    className="ai-reviewer-suggestions__implement-menu-item"
                    disabled={implementing}
                    onClick={async () => {
                      if (!onImplement || !selectedTask) return;
                      setImplementing(true);
                      try {
                        await onImplement(selectedTask, 'append');
                        await markTaskAsCompleted(selectedTask.uid);
                        setTasks(prev =>
                          prev.map(task =>
                            task.uid === selectedTask.uid
                              ? { ...task, is_implemented: true, status: 'completed' }
                              : task
                          )
                        );
                        setSelectedTask(null);
                      } catch (error) {
                        console.error('Failed to append with AI suggestion:', error);
                      } finally {
                        setImplementing(false);
                        setShowImplementMenu(false);
                      }
                    }}
                  >
                    {selectedTask.entity_type === 'skills_groups' ? 'Extend list' : 'Add to list'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {(isWorkAchievement || isVolunteerAchievement) ? (
                (() => {
                  const achievementState = isWorkAchievement ? workAchievementState : volunteerAchievementState;
                  return (
                    <div className="ai-reviewer-suggestions__implement-wrapper">
                      <button
                        className={`ai-reviewer-suggestions__implement-btn ${selectedTask?.is_implemented ? 'ai-reviewer-suggestions__implement-btn--implemented' : ''}`}
                        disabled={selectedTask?.is_implemented || implementing || !achievementState.canImplement}
                        onClick={async () => {
                          if (!onImplement || !selectedTask || selectedTask.is_implemented || !achievementState.canImplement) {
                            return;
                          }
                          setImplementing(true);
                          try {
                            await onImplement(selectedTask);
                            await markTaskAsCompleted(selectedTask.uid);
                            setTasks(prev =>
                              prev.map(task =>
                                task.uid === selectedTask.uid
                                  ? { ...task, is_implemented: true, status: 'completed' }
                                  : task
                              )
                            );
                            setSelectedTask(null);
                          } catch (error) {
                            console.error('Failed to implement achievement suggestion:', error);
                          } finally {
                            setImplementing(false);
                          }
                        }}
                      >
                        {achievementState.canImplement ? (
                          <>
                            <Check size={16} />
                            <span>{selectedTask?.is_implemented ? 'Implemented' : implementing ? 'Implementing...' : 'Implement'}</span>
                          </>
                        ) : (
                          <>
                            <Lock size={16} />
                            <span>Implement</span>
                          </>
                        )}
                      </button>
                      {!achievementState.canImplement && !selectedTask.is_implemented && (
                        <div className="ai-reviewer-suggestions__implement-tooltip">
                          {achievementState.tooltip}
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <button
                  className={`ai-reviewer-suggestions__implement-btn ${selectedTask?.is_implemented ? 'ai-reviewer-suggestions__implement-btn--implemented' : ''}`}
                  disabled={selectedTask?.is_implemented || implementing}
                  onClick={async () => {
                    if (onImplement && selectedTask && !selectedTask.is_implemented) {
                      setImplementing(true);
                      try {
                        await onImplement(selectedTask);
                        await markTaskAsCompleted(selectedTask.uid);
                        setTasks(prev =>
                          prev.map(task =>
                            task.uid === selectedTask.uid
                              ? { ...task, is_implemented: true, status: 'completed' }
                              : task
                          )
                        );
                        setSelectedTask(null);
                      } catch (error) {
                        console.error('Failed to implement:', error);
                      } finally {
                        setImplementing(false);
                      }
                    }
                  }}
                >
                  <Check size={16} />
                  <span>{selectedTask?.is_implemented ? 'Implemented' : implementing ? 'Implementing...' : 'Implement'}</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="ai-reviewer-suggestions">
        <div className="ai-reviewer-suggestions__empty">
          <div className="ai-reviewer-suggestions__empty-icon">
            <Sparkles size={48} />
          </div>
          <h3 className="ai-reviewer-suggestions__empty-title">No AI Suggestions Yet</h3>
          <p className="ai-reviewer-suggestions__empty-description">
            AI suggestions for your resume sections will appear here. Run a full review or trigger AI on a specific section.
          </p>

          <button
            className="ai-reviewer-suggestions__run-btn"
            onClick={runFullReview}
            disabled={runningFullReview}
            type="button"
          >
            {runningFullReview ? (
              <>
                <RefreshCw size={16} className="ai-reviewer-suggestions__run-spinner" />
                <span>Starting…</span>
              </>
            ) : (
              <>
                <Sparkles size={16} />
                <span>Run AI Review</span>
              </>
            )}
          </button>

          <div className="ai-reviewer-suggestions__empty-tips">
            <div className="ai-reviewer-suggestions__empty-tip">
              <Lightbulb size={16} />
              <span>
                Or click the <strong>AI Suggestions</strong> button inside any Summary, Work Experience, Volunteer or Skills section to review just that part.
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-reviewer-suggestions">
      <div className="ai-reviewer-suggestions__list">
        {visibleTasks.map((task) => {
          const isCompleted = task.status === 'completed';
          const isClickable = isCompleted;
          
          return (
            <div
              key={task.uid}
              className={`ai-reviewer-suggestions__item ${!isClickable ? 'ai-reviewer-suggestions__item--disabled' : ''}`}
              data-implemented={task.is_implemented}
              onClick={() => {
                if (isClickable) {
                  fetchTaskDetails(task.uid);
                }
              }}
            >
            <div className="ai-reviewer-suggestions__item-content">
              <div className="ai-reviewer-suggestions__item-header">
                <div className="ai-reviewer-suggestions__item-left">
                  <input
                    type="checkbox"
                    checked={task.is_implemented}
                    disabled={!isClickable}
                    onChange={(e) => {
                      e.stopPropagation();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="ai-reviewer-suggestions__item-checkbox"
                  />
                  <span className="ai-reviewer-suggestions__item-type">
                    {getEntityDisplayName(task)}
                  </span>
                </div>
                <div className="ai-reviewer-suggestions__item-right">
                  <span className={`ai-reviewer-suggestions__item-badge ${task.improvement_type === 'current' ? 'ai-reviewer-suggestions__item-badge--right' : ''}`}>
                    {getImprovementTypeLabel(task.improvement_type)}
                  </span>
                  <div 
                    className="ai-reviewer-suggestions__status-indicator"
                    style={{ backgroundColor: getStatusColor(task.status) }}
                    title={getStatusTooltip(task.status)}
                  >
                    <span className="ai-reviewer-suggestions__status-tooltip">
                      {getStatusTooltip(task.status)}
                    </span>
                  </div>
                </div>
              </div>
              {task.comment && (
                <div className="ai-reviewer-suggestions__item-comment">
                  {extractTextFromValue(task.comment)}
                </div>
              )}
            </div>
            {isClickable && (
              <ChevronRight size={16} className="ai-reviewer-suggestions__item-arrow" />
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
};
